// ApiKeysPage — Pro tier developer feature
// Layout:
//   - Top nav: < Back | "API Keys"
//   - Hero: tier-gating message
//   - List of existing keys (if Pro)
//   - "Create new key" CTA
//   - Documentation link

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TierPill, PartnerLogo } from '../../components/Brand';
import { getUser } from '../../lib/auth';

interface ApiKey {
  id: string;
  name: string;
  key: string;
  scope: 'read' | 'trade' | 'admin';
  lastUsed: string;
  created: string;
}

const EXISTING_KEYS: ApiKey[] = [
  { id: 'k1', name: 'My Trading Bot',    key: 'fp_live_sk_a1b2c3d4e5f6g7h8', scope: 'trade', lastUsed: '2026-07-24 11:32', created: '2026-07-04 10:00' },
  { id: 'k2', name: 'Dashboard (read)',   key: 'fp_live_rd_z9y8x7w6v5u4t3s2',  scope: 'read', lastUsed: '2026-07-23 18:45', created: '2026-07-04 10:00' },
];

const SCOPE_COLOR: Record<ApiKey['scope'], string> = {
  read:  'bg-tf-blue-tint text-tf-blue',
  trade: 'bg-tf-green-tint text-tf-profit',
  admin: 'bg-tf-red-tint text-tf-loss',
};

export function ApiKeysPage() {
  const nav = useNavigate();
  const user = getUser();
  const userTier = (user as any)?.tier || 'Beginner';
  const isPro = userTier === 'Professional';
  const [creating, setCreating] = useState(false);
  const [keys, setKeys] = useState(EXISTING_KEYS);
  const [visible, setVisible] = useState<Record<string, boolean>>({});

  function revoke(id: string) {
    if (confirm('Revoke this API key? Any app using it will lose access immediately.')) {
      setKeys((curr) => curr.filter((k) => k.id !== id));
    }
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">API Keys</div>
        {isPro ? (
          <button onClick={() => setCreating(true)} data-testid="new-key" className="px-3 text-[15px] font-semibold text-tf-blue">New</button>
        ) : (
          <div className="w-9" />
        )}
      </div>

      {/* Tier-gating message */}
      {!isPro && (
        <>
          <div className="mx-4 mt-4">
            <div className="rounded-[20px] border border-tf-border bg-white p-6 text-center" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F8F8FA 100%)' }}>
              <div className="flex justify-center mb-3">
                <TierPill tier="Professional" />
              </div>
              <div className="text-[22px] font-bold tracking-tight text-tf-text-1">API access is Pro only</div>
              <p className="mt-2 text-[14px] text-tf-text-3">Connect your own trading bots and analytics dashboards via our REST API. Available on the Professional plan.</p>
              <button
                onClick={() => nav('/portal/tiers')}
                data-testid="upgrade-pro"
                className="mt-4 flex h-12 w-full items-center justify-center rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.98]"
              >
                Upgrade to Professional
              </button>
            </div>
          </div>

          {/* API docs preview */}
          <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">What you can build</h2>
          <div className="mx-4 space-y-2">
            <Feature icon="🤖" title="Custom AI trading bots" sub="Use any language, any framework" />
            <Feature icon="📊" title="Analytics dashboards" sub="Stream live P&L, positions, fills" />
            <Feature icon="🔔" title="Webhook notifications" sub="Get push alerts on fills, draws" />
            <Feature icon="🔄" title="Cross-account sync" sub="Mirror trades across platforms" />
          </div>
        </>
      )}

      {/* Pro user — show keys */}
      {isPro && (
        <>
          {/* Hero */}
          <div className="mx-4 mt-4">
            <div className="rounded-[20px] border border-tf-border bg-white p-5" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F8F8FA 100%)' }}>
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-tf-text-1 text-[24px]">🔑</div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Active API keys</div>
                  <div className="dynamic-title text-tf-text-1 tabular-nums">{keys.length} of 10</div>
                </div>
              </div>
              <button
                onClick={() => setCreating(true)}
                data-testid="create-key"
                className="mt-4 flex h-12 w-full items-center justify-center gap-2 btn-spring"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg>
                Create new key
              </button>
            </div>
          </div>

          {/* Keys list */}
          <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Your keys</h2>
          <div className="mx-4 space-y-2">
            {keys.length === 0 ? (
              <div className="rounded-[16px] border border-tf-border bg-white p-6 text-center text-[14px] text-tf-text-3">No keys yet. Create one to get started.</div>
            ) : keys.map((k) => (
              <div key={k.id} data-testid={`key-${k.id}`} className="rounded-[16px] border border-tf-border bg-white p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[15px] font-bold text-tf-text-1">{k.name}</span>
                      <span className={['rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase', SCOPE_COLOR[k.scope]].join(' ')}>{k.scope}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-1.5">
                      <code className="rounded bg-tf-surface-1 px-2 py-0.5 text-[11px] font-mono text-tf-text-1">
                        {visible[k.id] ? k.key : k.key.slice(0, 12) + '••••••••••••••••'}
                      </code>
                      <button
                        onClick={() => setVisible((v) => ({ ...v, [k.id]: !v[k.id] }))}
                        className="text-[11px] font-semibold text-tf-blue"
                      >
                        {visible[k.id] ? 'Hide' : 'Reveal'}
                      </button>
                      <button onClick={() => { navigator.clipboard?.writeText(k.key); alert('Copied!'); }} className="text-[11px] font-semibold text-tf-blue">Copy</button>
                    </div>
                    <div className="mt-2 text-[11px] text-tf-text-3">
                      Last used: {k.lastUsed} · Created: {k.created}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => alert('Edit key name')} className="flex-1 h-9 rounded-full bg-tf-surface-1 text-[12px] font-semibold text-tf-text-1 active:opacity-40">Rename</button>
                  <button onClick={() => revoke(k.id)} data-testid={`revoke-${k.id}`} className="flex-1 h-9 rounded-full bg-tf-red-tint text-[12px] font-semibold text-tf-loss active:opacity-40">Revoke</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Powered by */}
      <div className="mx-4 mt-4 px-2">
        <div className="text-[10px] uppercase tracking-wider text-tf-text-3 text-center mb-2">API infrastructure by</div>
        <div className="flex items-center justify-center gap-4 opacity-80">
          <PartnerLogo name="tradingview" height={20} />
          <PartnerLogo name="twelvedata" height={20} />
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, title, sub }: { icon: string; title: string; sub: string }) {
  return (
    <div className="flex items-center gap-3 rounded-[14px] border border-tf-border bg-white p-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-tf-blue-tint text-[18px]">{icon}</div>
      <div>
        <div className="text-[14px] font-semibold text-tf-text-1">{title}</div>
        <div className="text-[11px] text-tf-text-3">{sub}</div>
      </div>
    </div>
  );
}
